<script setup lang="ts">
import type { UserProfile } from '@/types/user'

interface IProps {
  isLoading: boolean
  users: UserProfile[]
  isSearching?: boolean
  showAddAction?: boolean
  openInNewTab?: boolean
}

defineProps<IProps>()

const { colors } = useTheme()
const sessionUser = useUser()

const teamMembers = useTeamMembers()

const addItem = (user: UserProfile) => {
  teamMembers.value = [...teamMembers.value, user]
}
</script>

<template>
  <div v-if="users && users.length > 0" flex-grow>
    <div
      v-for="user in users"
      :key="user.id"
      flex gap-1 justify-between py-2 px-3
      bb-1 border-soft-gray
    >
      <NuxtLink
        :to="`/${user.username}`"
        :target="openInNewTab ? '_blank' : '_self'"
        flex flex-grow gap-3 items-center
      >
        <img
          loading="lazy"
          :src="optimizeImage(user.avatar, {
            width: 40 * 1.6,
            height: 40 * 1.6,
          })"
          referrerpolicy="no-referrer"
          w-10 h-10 rounded-full object-cover object-center
        >

        <div flex flex-col gap="2px">
          <Heading level="5" :color="colors.softBlack" :max-lines="1">
            {{ user.name }}
          </Heading>
          <Text variant="caption" :color="colors.bluishDarker" :max-lines="1">
            @{{ user.username }}
          </Text>
        </div>
      </NuxtLink>

      <div v-if="showAddAction" class="flex-shrink-0 pr-1">
        <AppButton
          variant="outline" type="button" h-10 pl-4 pr-4
          :enabled="teamMembers.findIndex(member => member.id === user.id) === -1"
          @click="teamMembers.findIndex(member => member.id === user.id) === -1 && addItem(user)"
        >
          <Heading level="5" color="inherit">
            {{ teamMembers.findIndex(member => member.id === user.id) > -1 ? 'Invited' : 'Invite' }}
          </Heading>
        </AppButton>
      </div>
      <template v-else>
        <UserFollowButton v-if="user.id !== sessionUser?.id" :user="user" />
      </template>
    </div>
  </div>

  <Text v-else key="not-found" variant="small" text-bluish-softer text-center mt-5 mb-4>
    <slot v-if="!isSearching" name="not-searching">
      Start typing to search.
    </slot>
    <slot v-else-if="isLoading" name="loading">
      Loading...
    </slot>
    <slot v-else name="not-found">
      No users found.
    </slot>
  </Text>
</template>
